import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DropdownInputInterface } from '../../../../interfaces/dropdown-input.interface';
import { Currency } from '../../../../interfaces/idefend/currency.interface';
import { LocalizationSetting } from '../../../../interfaces/idefend/localization-settings.interface';
import { PolicyGroupResponse } from '../../../../interfaces/idefend/policy-groups-response.interface';
import { PortfolioGroups } from '../../../../interfaces/idefend/portfolio-group.interface';
import { Portfolio } from '../../../../interfaces/idefend/portfolio.interface';
import { SodanReplayHeads } from '../../../../interfaces/idefend/sodan-replay-heads.interface';
import { StepsValidators } from '../../../../interfaces/idefend/steps-validators.interface';
import { VehicleMake } from '../../../../interfaces/idefend/vehicle-make.interface';
import { VehicleModel } from '../../../../interfaces/idefend/vehicle-model.interface';
import { VehicleUsage } from '../../../../interfaces/idefend/vehicle-usage.interface';
import { VehiclesCategory } from '../../../../interfaces/idefend/vehilces-category.interface';
import { CalculateOfferPayload } from '../../shared/interfaces/calculate-offer-payload.interface';
import { FirstStepPayload } from '../../shared/interfaces/first-step-payload.interface';
import { FourthStepPayload } from '../../shared/interfaces/fourth-step-payload.interface';
import { SecondStepPayload } from '../../shared/interfaces/second-step-payload.interface';
import { ThirdStepPayload } from '../../shared/interfaces/third-step-payload.interface';
import { offerPayloadMapper } from '../helpers/offer-payload-mapper';
import { PolicyCreatorService } from '../policy-creator.service';
import { OfferState } from './offer.state';
import { CacheService } from '../../../services/cache.service';

import {
  FilterVehicleModelsByCategory,
  FilterVehicleModelsByMake,
  GetCurrency,
  GetGroups,
  GetLocalizationSettings,
  GetPortfolios,
  GetVehicleCategories,
  GetVehicleMakes,
  GetVehicleModels,
  GetVehicleUsages,
  PatchFirstStepFormValues,
  PatchFourthStepFormValues,
  PatchPaymentMethod,
  PatchPaymentPeriod,
  PatchQuestionReply,
  PatchSecondStepFormValues,
  PatchThirdStepFormValues,
  PopulateStateFromCreationLock,
  ResetPolicyCreatorState,
  SetActiveStep,
  SetEmptySodanReplyHeads,
  SetPolicyGroups,
  SetStepsValidators,
} from './policy-creator.actions';
import { defaults, PolicyCreatorStateModel } from './policy-creator.model';
import { deepEqual } from '../../warranty-creator/helpers/deep-equal';
import { ResetOfferState } from './offer.action';

@State<PolicyCreatorStateModel>({
  name: 'policyCreatorState',
  defaults,
})
@Injectable()
export class PolicyCreatorState {
  // caching system disabled, enable after writing complex e2e tests which can be run at the test environment
  private readonly useCache = false;
  private readonly VEHICLE_CATEGORIES_CACHE_KEY = 'vehicle_categories';
  private readonly VEHICLE_USAGES_CACHE_KEY = 'vehicle_usages';
  private readonly SODAN_REPLY_HEADS_CACHE_KEY = 'sodan_reply_heads';
  private readonly QUESTION_REPLY_CACHE_KEY = 'question_reply';
  private readonly PORTFOLIOS_CACHE_KEY = 'portfolios';
  constructor(
    private store: Store,
    private policyCreatorService: PolicyCreatorService,
    private cacheService: CacheService,
  ) {}

  @Selector()
  static state(state: PolicyCreatorStateModel): PolicyCreatorStateModel {
    return state;
  }

  @Selector()
  static portfolios({ portfolios }: PolicyCreatorStateModel): Portfolio[] {
    return portfolios;
  }

  @Selector()
  static portfolioSelected({
    portfolioSelected,
  }: PolicyCreatorStateModel): Portfolio {
    return portfolioSelected;
  }

  @Selector()
  static groups({ groups }: PolicyCreatorStateModel): PortfolioGroups[] {
    return groups;
  }

  @Selector()
  static currency({ currency }: PolicyCreatorStateModel): Currency {
    return currency;
  }

  @Selector()
  static localizationSettings({
    localizationSettings,
  }: PolicyCreatorStateModel): LocalizationSetting {
    return localizationSettings;
  }

  @Selector()
  static sodanReplyHeads({
    sodanReplyHeads,
  }: PolicyCreatorStateModel): SodanReplayHeads {
    return sodanReplyHeads;
  }

  @Selector()
  static policyGroups({
    policyGroups,
  }: PolicyCreatorStateModel): PolicyGroupResponse {
    return policyGroups;
  }

  @Selector()
  static vehicleCategories({
    vehicleCategories,
  }: PolicyCreatorStateModel): VehiclesCategory[] {
    return vehicleCategories;
  }

  @Selector()
  static vehicleUsages({
    vehicleUsages,
  }: PolicyCreatorStateModel): VehicleUsage[] {
    return vehicleUsages;
  }

  @Selector()
  static vehicleType({
    firstStepPayload,
  }: PolicyCreatorStateModel): DropdownInputInterface {
    return firstStepPayload.vehicleType;
  }

  @Selector()
  static vehicleRegDate({ firstStepPayload }: PolicyCreatorStateModel): string {
    return firstStepPayload.vehicleRegDate;
  }

  @Selector()
  static vehicleMakes({
    vehicleMakes,
  }: PolicyCreatorStateModel): VehicleMake[] {
    return vehicleMakes;
  }

  @Selector()
  static vehicleModels({
    vehicleModels,
  }: PolicyCreatorStateModel): VehicleModel[] {
    return vehicleModels;
  }

  @Selector()
  static vehicleModelsFilter({
    vehicleModelsFilter,
  }: PolicyCreatorStateModel): VehicleModel[] {
    return vehicleModelsFilter;
  }

  @Selector()
  static haveAcPolicy({ firstStepPayload }: PolicyCreatorStateModel): boolean {
    return firstStepPayload.haveACPolicy;
  }

  @Selector()
  static boughtSince180Days({
    firstStepPayload,
  }: PolicyCreatorStateModel): boolean {
    return firstStepPayload.boughtSince180Days;
  }

  @Selector()
  static vehicleBuyDate({ firstStepPayload }: PolicyCreatorStateModel): string {
    return firstStepPayload.vehicleBuyDate;
  }

  @Selector()
  static acPolicyRenewalDate({
    firstStepPayload,
  }: PolicyCreatorStateModel): string {
    return firstStepPayload.acPolicyRenewalDate;
  }

  @Selector()
  static selectedInsuranceVariant({
    firstStepPayload,
  }: PolicyCreatorStateModel): string {
    return firstStepPayload.selectedInsuranceVariant;
  }

  @Selector()
  static firstStepPayload({
    firstStepPayload,
  }: PolicyCreatorStateModel): FirstStepPayload {
    return firstStepPayload;
  }

  @Selector()
  static secondStepPayload({
    secondStepPayload,
  }: PolicyCreatorStateModel): SecondStepPayload {
    return secondStepPayload;
  }

  @Selector()
  static thirdStepPayload({
    thirdStepPayload,
  }: PolicyCreatorStateModel): ThirdStepPayload {
    return thirdStepPayload;
  }

  @Selector()
  static fourthStepPayload({
    fourthStepPayload,
  }: PolicyCreatorStateModel): FourthStepPayload {
    return fourthStepPayload;
  }

  @Selector()
  static paymentMethod({ paymentMethod }: PolicyCreatorStateModel): string {
    return paymentMethod;
  }

  @Selector()
  static fetchCalculateOfferPayload(
    state: PolicyCreatorStateModel,
  ): CalculateOfferPayload {
    return offerPayloadMapper(state);
  }

  @Selector()
  static getActiveStep(state: PolicyCreatorStateModel): number {
    return state.activeStep;
  }

  @Selector()
  static getStepsValidators(state: PolicyCreatorStateModel): StepsValidators {
    return state.stepsValidator;
  }

  @Action(GetPortfolios)
  public getPortfolios({ patchState }: StateContext<PolicyCreatorStateModel>) {
    const cachedData = this.cacheService.getSessionData<Portfolio[]>(
      this.PORTFOLIOS_CACHE_KEY,
    );

    if (cachedData && this.useCache) {
      patchState({ portfolios: cachedData });
      return;
    }

    return this.policyCreatorService.getPortfolios().pipe(
      tap((portfolios) => {
        if (this.useCache) {
          // Store in cache
          this.cacheService.setSessionData(
            this.PORTFOLIOS_CACHE_KEY,
            portfolios,
          );
        }

        patchState({
          portfolios,
        });
      }),
    );
  }

  @Action(GetGroups)
  public getGroups({ patchState }: StateContext<PolicyCreatorStateModel>) {
    return this.policyCreatorService.getGroups().pipe(
      tap((groups) => {
        patchState({ groups: groups });
      }),
    );
  }

  @Action(GetCurrency)
  public getCurrency(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { id }: GetCurrency,
  ) {
    return this.policyCreatorService.getCurrency(id).pipe(
      tap((currency) => {
        patchState({ currency });
      }),
    );
  }

  @Action(GetLocalizationSettings)
  public getLocalizationSettings(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { id }: GetLocalizationSettings,
  ) {
    return this.policyCreatorService.getLocalizationSettings(id).pipe(
      tap((localizationSettings) => {
        patchState({ localizationSettings });
      }),
    );
  }

  @Action(SetEmptySodanReplyHeads)
  public setEmptySodanReplyHeads(ctx: StateContext<PolicyCreatorStateModel>) {
    // Try to get data from cookie first
    const cachedData = this.cacheService.getSessionData<SodanReplayHeads>(
      this.SODAN_REPLY_HEADS_CACHE_KEY,
    );

    if (cachedData && this.useCache) {
      const state = ctx.getState();
      ctx.setState({
        ...state,
        sodanReplyHeads: cachedData,
      });
      return;
    }

    // If no cached data, fetch from API
    return this.policyCreatorService.setSodanReplyHeads().pipe(
      tap((sodanReplyHeads) => {
        if (this.useCache) {
          // Store in cache
          this.cacheService.setSessionData(
            this.SODAN_REPLY_HEADS_CACHE_KEY,
            sodanReplyHeads,
          );
        }

        const state = ctx.getState();
        ctx.setState({
          ...state,
          sodanReplyHeads: sodanReplyHeads,
        });
      }),
    );
  }

  @Action(SetPolicyGroups)
  public setPolicyGroups(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { payload }: SetPolicyGroups,
  ) {
    return this.policyCreatorService.setPolicyGroups(payload).pipe(
      tap((policyGroups) => {
        patchState({ policyGroups });
      }),
    );
  }

  @Action(PatchQuestionReply)
  public patchQuestionReply(
    { getState, patchState }: StateContext<PolicyCreatorStateModel>,
    { payload, query, id }: PatchQuestionReply,
  ) {
    // Try to get data from cookie first
    const cachedData = this.cacheService.getSessionData<SodanReplayHeads>(
      this.QUESTION_REPLY_CACHE_KEY,
    );

    if (cachedData && this.useCache) {
      const sodanReplyHeadsDto = cachedData;
      sodanReplyHeadsDto.replies = sodanReplyHeadsDto.replies.map((reply) => {
        if (reply.id === id) {
          return { ...reply, ...payload };
        }
        return reply;
      });
      patchState({
        sodanReplyHeads: sodanReplyHeadsDto,
      });
      return;
    }

    // If no cached data, fetch from API
    return this.policyCreatorService
      .patchQuestionReply(payload, query, id)
      .pipe(
        tap((replyResponse) => {
          const sodanReplyHeadsDto = getState().sodanReplyHeads;
          sodanReplyHeadsDto.replies = sodanReplyHeadsDto.replies.map(
            (reply) => {
              if (reply.id === replyResponse.id) {
                return replyResponse;
              }
              return reply;
            },
          );

          if (this.useCache) {
            // Store in cache
            this.cacheService.setSessionData(
              this.QUESTION_REPLY_CACHE_KEY,
              sodanReplyHeadsDto,
            );
          }

          patchState({
            sodanReplyHeads: sodanReplyHeadsDto,
          });
        }),
      );
  }

  @Action(GetVehicleCategories)
  public getVehicleCategories(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { query }: GetVehicleCategories,
  ) {
    // Try to get data from cache first
    // const cachedData = this.cacheService.getSessionData<VehiclesCategory[]>(
    //   this.VEHICLE_CATEGORIES_CACHE_KEY,
    // );

    // if (cachedData) {
    //   patchState({ vehicleCategories: cachedData });
    //   return;
    // }

    // If no cached data, fetch from API
    return this.policyCreatorService.getVehicleCategories(query).pipe(
      tap((vehicleCategories) => {
        // Store in cache
        this.cacheService.setSessionData(
          this.VEHICLE_CATEGORIES_CACHE_KEY,
          vehicleCategories,
        );
        patchState({ vehicleCategories });
      }),
    );
  }

  @Action(GetVehicleUsages)
  public getVehicleUsages(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { query }: GetVehicleUsages,
  ) {
    // Try to get data from cache first
    // const cachedData = this.cacheService.getSessionData<VehicleUsage[]>(
    //   this.VEHICLE_USAGES_CACHE_KEY,
    // );

    // if (cachedData) {
    //   patchState({ vehicleUsages: cachedData });
    //   return;
    // }

    // If no cached data, fetch from API
    return this.policyCreatorService.getVehicleUsages(query).pipe(
      tap((vehicleUsages) => {
        // Store in cache
        this.cacheService.setSessionData(
          this.VEHICLE_USAGES_CACHE_KEY,
          vehicleUsages,
        );
        patchState({ vehicleUsages });
      }),
    );
  }

  @Action(GetVehicleMakes)
  public getVehicleMakes({
    patchState,
  }: StateContext<PolicyCreatorStateModel>) {
    return this.policyCreatorService.getVehicleMakes().pipe(
      tap((vehicleMakes) => {
        patchState({ vehicleMakes });
      }),
    );
  }

  @Action(GetVehicleModels)
  public getVehicleModels({
    patchState,
  }: StateContext<PolicyCreatorStateModel>) {
    return this.policyCreatorService.getVehicleModels().pipe(
      tap((vehicleModels) => {
        patchState({ vehicleModels });
      }),
    );
  }

  @Action(FilterVehicleModelsByCategory)
  public filterVehicleModelsByCategory(
    { getState, patchState }: StateContext<PolicyCreatorStateModel>,
    { categoryId, categoryCode }: FilterVehicleModelsByCategory,
  ) {
    const vehicleMakesReq =
      this.policyCreatorService.getVehicleMakes(categoryCode);
    const vehicleModelsReq = this.policyCreatorService.getVehicleModels();

    return combineLatest([vehicleMakesReq, vehicleModelsReq]).pipe(
      tap(([vehicleMakes, vehicleModels]) => {
        const vehicleModelsFilter = vehicleModels.filter(
          (model) => model.categoryId === categoryId,
        );
        patchState({
          vehicleMakes: vehicleMakes,
          vehicleModels: vehicleModelsFilter,
        });
      }),
    );
  }

  @Action(FilterVehicleModelsByMake)
  public filterVehicleModelsByMake(
    { getState, patchState }: StateContext<PolicyCreatorStateModel>,
    { selectedVehicleMake }: FilterVehicleModelsByMake,
  ) {
    const vehicleModelsFilter = getState().vehicleModels.filter(
      (model) => model.makeId === selectedVehicleMake.id,
    );
    patchState({ vehicleModelsFilter: vehicleModelsFilter });
  }

  @Action(PatchFirstStepFormValues)
  public patchFirstStepFormValues(
    { getState, patchState }: StateContext<PolicyCreatorStateModel>,
    { firstStepPayload }: PatchFirstStepFormValues,
  ) {
    const currentFirstStepPayload = getState().firstStepPayload;
    if (
      currentFirstStepPayload &&
      deepEqual(currentFirstStepPayload, firstStepPayload)
    ) {
      return;
    } else {
      if (firstStepPayload.selectedInsuranceVariant) {
        const portfolioFound = getState().portfolios.find(
          (portfolio) =>
            portfolio.productDerivativeAlias ===
            firstStepPayload.selectedInsuranceVariant,
        );
        if (portfolioFound) {
          patchState({
            portfolioSelected: portfolioFound,
          });
        }
      }

      const offer = this.store.selectSnapshot(OfferState.offer);
      if (offer) {
        this.store.dispatch(new ResetOfferState());
      }
      patchState({ firstStepPayload });
    }
  }

  @Action(PatchSecondStepFormValues)
  public patchSecondStepFormValues(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { secondStepPayload }: PatchSecondStepFormValues,
  ) {
    patchState({ secondStepPayload });
  }

  @Action(PatchThirdStepFormValues)
  public patchThirdStepFormValues(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { thirdStepPayload }: PatchThirdStepFormValues,
  ) {
    patchState({ thirdStepPayload });
  }

  @Action(PatchFourthStepFormValues)
  public patchFourthStepFormValues(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { fourthStepPayload }: PatchFourthStepFormValues,
  ) {
    const premiumSuggested = this.store.selectSnapshot(
      OfferState.offer,
    )?.premiumSuggested;
    if (premiumSuggested) {
      patchState({
        fourthStepPayload: {
          ...fourthStepPayload,
          premiumSuggested: premiumSuggested,
        },
      });
    }
  }

  @Action(PatchPaymentMethod)
  public patchPaymentMethod(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { paymentMethod }: PatchPaymentMethod,
  ) {
    patchState({ paymentMethod });
  }

  @Action(SetActiveStep)
  public setActiveStep(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { step }: SetActiveStep,
  ) {
    patchState({
      activeStep: step,
    });
  }

  @Action(ResetPolicyCreatorState)
  public resetState({ setState }: StateContext<PolicyCreatorStateModel>) {
    return setState(defaults);
  }

  @Action(SetStepsValidators)
  public setStepsValidators(
    { getState, patchState }: StateContext<PolicyCreatorStateModel>,
    { stepValidator }: SetStepsValidators,
  ) {
    const stepsValidatorState = getState().stepsValidator;

    return patchState({
      stepsValidator: {
        ...stepsValidatorState,
        ...stepValidator,
      },
    });
  }

  @Action(PopulateStateFromCreationLock)
  public populateStateFromCreationLock(
    { patchState }: StateContext<PolicyCreatorStateModel>,
    { creationLockResponse }: PopulateStateFromCreationLock,
  ) {
    console.log(
      '[Policy Creator State] Populating state from CreationLockResponse',
      {
        policyNumber: creationLockResponse.number,
        productCode: creationLockResponse.productCode,
      },
    );

    try {
      // Create FirstStepPayload from CreationLockResponse
      const firstStepPayload: FirstStepPayload = {
        vehicleType: null, // This might need to be derived from vehicleSnapshot
        vehicleRegDate: creationLockResponse.vehicleSnapshot?.registeredOn,
        acPolicyRenewalDate: null,
        haveACPolicy: false,
        boughtSince180Days: false,
        selectedVehicleMake: null, // Need to be matched with the make code
        selectedVehicleModel: null, // Need to be matched with the model code
        vehicleBuyDate: creationLockResponse.vehicleSnapshot?.purchasedOn,
        vehiclePrice: creationLockResponse.vehicleSnapshot?.value || 0,
        vehiclePriceAc: 0,
        vehiclePriceType: 'NET',
        vehiclePolicyPrice: creationLockResponse.premiumSuggested / 100, // Adjust according to your pricing format
        vehiclePolicyPriceType: 'NET',
        selectedInsuranceVariant: creationLockResponse.productCode,
        manufacturerWarranty: false,
        mfrWarrantyPeriod: null,
        mfrWarrantyMileage: null,
        mfrWarrantyInceptedOn: null,
        customMfrWarrantyMileage: null,
      };

      // Create SecondStepPayload from CreationLockResponse
      const secondStepPayload: SecondStepPayload = {
        policyPeriod: creationLockResponse.options?.PERIOD,
        insuranceLimit: creationLockResponse.options?.SUM_INSURED,
        paymentType: creationLockResponse.options?.PAYMENT_TERM || 'PT_LS',
        paymentForm: null,
        paymentPeriod: 1, // Default value
      };

      // Create ThirdStepPayload from CreationLockResponse
      const thirdStepPayload: ThirdStepPayload = {
        insurer: {
          clientType:
            creationLockResponse.client?.policyHolder?.type || 'INDIVIDUAL',
          firstName: creationLockResponse.client?.policyHolder?.firstName || '',
          lastName: creationLockResponse.client?.policyHolder?.lastName || '',
          companyName:
            creationLockResponse.client?.policyHolder?.companyName || '',
          idNumber: creationLockResponse.client?.policyHolder?.idNumber || '',
          address1:
            creationLockResponse.client?.policyHolder?.address?.address1 || '',
          address2:
            creationLockResponse.client?.policyHolder?.address?.address2 || '',
          postalCode:
            creationLockResponse.client?.policyHolder?.address?.postalCode ||
            '',
          city: creationLockResponse.client?.policyHolder?.address?.city || '',
          phonePrefix: 48, // Default Polish prefix
          phone: parseInt(
            creationLockResponse.client?.policyHolder?.phoneNumber?.replace(
              /\D/g,
              '',
            ) || '0',
          ),
          email: creationLockResponse.client?.policyHolder?.email || '',
          sameAsInsurer: true,
        },
        insured: {
          clientType:
            creationLockResponse.client?.insured?.type || 'INDIVIDUAL',
          firstName: creationLockResponse.client?.insured?.firstName || '',
          lastName: creationLockResponse.client?.insured?.lastName || '',
          companyName: creationLockResponse.client?.insured?.companyName || '',
          idNumber: creationLockResponse.client?.insured?.idNumber || '',
          address1:
            creationLockResponse.client?.insured?.address?.address1 || '',
          address2:
            creationLockResponse.client?.insured?.address?.address2 || '',
          postalCode:
            creationLockResponse.client?.insured?.address?.postalCode || '',
          city: creationLockResponse.client?.insured?.address?.city || '',
          phonePrefix: 48, // Default Polish prefix
          phone: parseInt(
            creationLockResponse.client?.insured?.phoneNumber?.replace(
              /\D/g,
              '',
            ) || '0',
          ),
          email: creationLockResponse.client?.insured?.email || '',
          sameAsInsurer:
            creationLockResponse.client?.insured?.sameAsInsurer || false,
        },
      };

      // Create FourthStepPayload from CreationLockResponse
      const fourthStepPayload: FourthStepPayload = {
        vin: creationLockResponse.vehicleSnapshot?.vin || '',
        millage: creationLockResponse.vehicleSnapshot?.odometerReading || 0,
        registryNo: creationLockResponse.vehicleSnapshot?.registrationNo || '',
        insuranceStartDate: creationLockResponse.inceptionDate || '',
        sameAsInsurer: true,
        owner: {
          clientType: creationLockResponse.client?.owner?.type || 'INDIVIDUAL',
          firstName: creationLockResponse.client?.owner?.firstName || '',
          lastName: creationLockResponse.client?.owner?.lastName || '',
          companyName: creationLockResponse.client?.owner?.companyName || '',
          idNumber: creationLockResponse.client?.owner?.idNumber || '',
          address1: creationLockResponse.client?.owner?.address?.address1 || '',
          address2: creationLockResponse.client?.owner?.address?.address2 || '',
          postalCode:
            creationLockResponse.client?.owner?.address?.postalCode || '',
          city: creationLockResponse.client?.owner?.address?.city || '',
          phonePrefix: 48, // Default Polish prefix
          phone: parseInt(
            creationLockResponse.client?.owner?.phoneNumber?.replace(
              /\D/g,
              '',
            ) || '0',
          ),
          email: creationLockResponse.client?.owner?.email || '',
          sameAsInsurer:
            creationLockResponse.client?.owner?.sameAsInsurer || false,
        },
        premiumMin: creationLockResponse.premiumMin / 100,
        premiumSuggested: creationLockResponse.premiumSuggested / 100,
      };

      // Determine payment method from response
      let paymentMethod = 'PM_PAYU_M'; // Default - could be updated based on the data available

      // Populate state with extracted data
      patchState({
        firstStepPayload,
        secondStepPayload,
        thirdStepPayload,
        fourthStepPayload,
        paymentMethod,
        stepsValidator: {
          first: true,
          second: true,
          third: true,
          fourth: true,
        },
      });

      // Find and set the selected portfolio based on the product code
      const portfolios = this.store.selectSnapshot(
        PolicyCreatorState.portfolios,
      );
      if (portfolios && portfolios.length > 0) {
        const selectedPortfolio = portfolios.find(
          (p) =>
            p.productDerivativeAlias === creationLockResponse.productCode ||
            p.productCode === creationLockResponse.productCode,
        );

        if (selectedPortfolio) {
          patchState({ portfolioSelected: selectedPortfolio });
        }
      }

      console.log(
        '[Policy Creator State] Successfully populated state from CreationLockResponse',
      );
    } catch (error) {
      console.error(
        '[Policy Creator State] Error populating state from CreationLockResponse',
        error,
      );
    }
  }
}
